import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material';
import { GetTaskPoolQuery } from '../../gql/graphql';
import { CoreTaskStatusChoices } from '../../gql/graphql';
import { useWithPreviousState } from '../../hooks/useWithPreviousState';

// ----------------------------------------------------------------------

const getProgress = (
  taskPool: NonNullable<GetTaskPoolQuery['getTaskPool']> | null | undefined,
): number => {
  if (!taskPool?.tasks || taskPool.tasks.length === 0) {
    return 0;
  }

  const totalTasks = taskPool.tasks.length;
  // Treat FAILED as "finished" so optional task failures still advance progress.
  const finishedTasks = taskPool.tasks.filter(
    (task) =>
      task?.status === CoreTaskStatusChoices.Completed ||
      task?.status === CoreTaskStatusChoices.Failed,
  ).length;

  return (finishedTasks / totalTasks) * 100;
};

interface TaskPoolProgressProps {
  taskPool: NonNullable<GetTaskPoolQuery['getTaskPool']> | null | undefined;
  onPercentageChange?: (percentage: number) => void;
  showTaskDetails?: boolean;
}

export const TaskPoolProgress = ({
  taskPool,
  onPercentageChange,
  showTaskDetails = true,
}: TaskPoolProgressProps) => {
  const [{ value: progress, previous }, setProgress] =
    useWithPreviousState<number>(0);
  const [sortedTasks, setSortedTasks] = useState<
    NonNullable<GetTaskPoolQuery['getTaskPool']>['tasks']
  >([]);

  useEffect(() => {
    if (taskPool) {
      const newProgress = getProgress(taskPool);
      setProgress(newProgress);
      const tasks = taskPool.tasks || [];

      // Sort tasks by order if available
      const sorted = [...tasks].sort((a, b) => {
        return (a.order ?? 0) - (b.order ?? 0);
      });
      setSortedTasks(sorted);
    } else {
      setProgress(0);
      setSortedTasks([]);
    }
  }, [taskPool, setProgress]);

  // Call onPercentageChange only when percentage actually changes
  useEffect(() => {
    if (previous !== undefined && previous !== progress) {
      onPercentageChange?.(progress);
    }
  }, [progress, previous, onPercentageChange]);

  return (
    <Collapse in={!!taskPool}>
      <TaskPoolProgressContainer>
        <ProgressLabel>Progress: {Math.round(progress)}%</ProgressLabel>
        <ProgressBar variant='determinate' value={progress} />
        {showTaskDetails
          ? sortedTasks.map((task) => {
              const isCompleted =
                task?.status === CoreTaskStatusChoices.Completed;
              const isPending = task?.status === CoreTaskStatusChoices.Pending;
              const isFailed = task?.status === CoreTaskStatusChoices.Failed;

              return (
                <TaskItem
                  key={task?.id}
                  $isFailed={isFailed}
                  $isCompleted={isCompleted}
                >
                  {isCompleted ? '✓' : isPending ? '⋯' : isFailed ? '✗' : '○'}{' '}
                  {task?.description || 'Unknown task'}
                  {task?.numSubtasks && task.numSubtasks > 0
                    ? ` (${task.numCompletedSubtasks || 0}/${task.numSubtasks})`
                    : ''}
                </TaskItem>
              );
            })
          : null}
      </TaskPoolProgressContainer>
    </Collapse>
  );
};

const TaskPoolProgressContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const ProgressLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

interface TaskItemProps {
  $isFailed: boolean;
  $isCompleted: boolean;
}

const TaskItem = styled(Typography)<TaskItemProps>(
  ({ theme, $isFailed, $isCompleted }) => ({
    fontSize: '0.9rem',
    color: $isFailed
      ? theme.palette.error.main
      : $isCompleted
      ? theme.palette.text.primary
      : theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  }),
);
